/** What a catalogue product's own row says about joins (plan 2026-10-11 product joins §9, B8b),
 *  shown in its settings drawer under `tour.join`:
 *
 *  - **Joined by** (`joined_by`): the products whose `tour.join` names this one. Their IDs are
 *    additional IDs of its tour, so it must keep that one tour: a change that would take it away
 *    (not synced, no tour, a tour per option) is refused `join_target_in_use` until their joins are
 *    cleared. Each joiner opens its own drawer.
 *  - **Splits off at its next sync** (`pending_split_off`): its join was cleared while its IDs sat
 *    on the target's tour. Its next sync builds a tour of its own keyed by its ID, and moves that ID
 *    and its other IDs there; until then its bookings keep landing on the old tour. Joining again
 *    cancels it (the server drops the marker). A product set to one tour per option is not split
 *    off: the split-off moves one tour key, so the marker stays until it is one tour again.
 *
 *  And the tenant-defaults form's `join_target_in_use` refusal (`TargetsInUseAlert`): a default that
 *  would take the one tour away from products others join, each named with its joiners. */
import { Alert, AlertTitle, Box, Chip, Link } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { Fragment, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as catalogueApi from '@/api/catalogue';
import {
  catalogueKeys,
  type CatalogueProduct,
  type CatalogueRow,
  type CatalogueScope,
  type PendingSplitOff,
  type SettingError,
} from '@/api/catalogue';
import { formatDate } from '@/utils/format';

import { joinerLabel, joinersPhrase, KEY } from './catalogueModel';
import { useCatalogueProductRef, useCatalogueProductRefs } from './queries';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const Code = ({ children }: { children: ReactNode }) => (
  <Box component="code" sx={{ fontFamily: MONO }}>
    {children}
  </Box>
);

/** The tour a pending split-off leaves, in words. */
export function splitOffFromLabel(p: Pick<PendingSplitOff, 'from_tour_id' | 'from_tour_name'>): string {
  return p.from_tour_name ? `${p.from_tour_name} (#${p.from_tour_id})` : `tour #${p.from_tour_id}`;
}

/** A pending split-off in plain sentences (the grid chip's tooltip). */
export function pendingSplitOffSentence(p: PendingSplitOff): string {
  const ids = [p.key, ...p.aliases];
  const by = p.set_by != null ? ` by user #${p.set_by}` : '';
  const when = p.set_ts ? ` on ${formatDate(p.set_ts)}` : '';
  return (
    `Its join${p.into ? ` of ${p.into}` : ''} was cleared${by}${when}. At its next sync it gets a tour of its own ` +
    `keyed ${p.key}, and its ${ids.length === 1 ? 'ID' : 'IDs'} on ${splitOffFromLabel(p)} ` +
    `${ids.length === 1 ? 'moves' : 'move'} there: ` +
    `${ids.join(', ')}. Joining a product again cancels this.`
  );
}

/** A tour, linked to its page in a new tab (the drawer's draft stays). */
export function TourLink({ id, name }: { id: number; name?: string | null }) {
  return (
    <Link component={RouterLink} to={`/tours/${id}`} target="_blank" rel="noopener">
      {name ? `${name} (#${id})` : `tour #${id}`}
    </Link>
  );
}

/** How many joiners the drawer looks up by name (the rest show their vendor id). */
export const JOINERS_NAMED = 5;

/** The rows of `product`'s first joiners (`JOINERS_NAMED`), looked up by vendor id in its
 *  catalogue: vendor id -> row (null when the catalogue has none; absent while loading). */
export function useJoinerRows(
  product: Pick<CatalogueProduct, 'joined_by'> | undefined,
  scope: CatalogueScope | null,
): ReadonlyMap<string, CatalogueRow | null> {
  return useCatalogueProductRefs(scope, (product?.joined_by ?? []).slice(0, JOINERS_NAMED));
}

export function JoinedByNote({
  product,
  joinerRows,
  onOpenProduct,
}: {
  product: CatalogueProduct;
  /** Its joiners' rows (`useJoinerRows`). */
  joinerRows: ReadonlyMap<string, CatalogueRow | null>;
  /** Open a joiner's drawer. */
  onOpenProduct: (productId: number) => void;
}) {
  const joiners = product.joined_by;
  if (!joiners.length) return null;
  const n = joiners.length;
  return (
    <Alert severity="info" sx={{ mt: 1, '& .MuiAlert-message': { width: '100%' } }} aria-label="Joined by">
      <AlertTitle>
        Joined by {n} product{n === 1 ? '' : 's'}
      </AlertTitle>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.75 }}>
        {joiners.map((v) => {
          const row = joinerRows.get(v);
          return (
            <Chip
              key={v}
              size="small"
              variant="outlined"
              label={joinerLabel(v, row)}
              {...(row ? { onClick: () => onOpenProduct(row.id) } : {})}
              sx={{ height: 22, fontSize: 12 }}
            />
          );
        })}
      </Box>
      {n === 1 ? 'Its' : 'Their'} product and option IDs are additional IDs of this product&apos;s tour, so{' '}
      {n === 1 ? 'its' : 'their'} bookings land on it. While {n === 1 ? 'it joins' : 'they join'}, this product
      keeps one tour for the product: excluding it, or a Kind, Tours or Package import that leaves it no tour
      (or a tour per option), is refused. Clear {n === 1 ? 'its join' : 'their joins'} first.
    </Alert>
  );
}

export function PendingSplitOffNote({
  product,
  scope,
  joining,
}: {
  product: CatalogueProduct;
  /** The product's catalogue (the cleared join's target is named from it). */
  scope: CatalogueScope;
  /** The draft sets a join: saving it cancels the split-off. */
  joining: boolean;
}) {
  const pending = product.pending_split_off;
  const into = useCatalogueProductRef(pending?.into ? scope : null, pending?.into ?? null);
  if (!pending) return null;
  const intoName = into.data ? joinerLabel(pending.into!, into.data) : pending.into;
  const ids = [pending.key, ...pending.aliases];
  const perOption = product.resolution.product[KEY.grain]?.value === 'option';
  return (
    <Alert
      severity={perOption ? 'warning' : 'info'}
      sx={{ mt: 1, '& .MuiAlert-message': { width: '100%' } }}
      aria-label="Pending split-off"
    >
      <AlertTitle>Splits off at its next sync</AlertTitle>
      <div>
        Its join{intoName ? ` of ${intoName}` : ''} was cleared
        {pending.set_by != null && (
          <>
            {' '}
            by{' '}
            <Link component={RouterLink} to={`/users/${pending.set_by}`} target="_blank" rel="noopener">
              user #{pending.set_by}
            </Link>
          </>
        )}
        {pending.set_ts && ` on ${formatDate(pending.set_ts)}`}.
      </div>
      <div>
        At its next sync it gets a tour of its own, keyed <Code>{pending.key}</Code>, and its{' '}
        {ids.length === 1 ? 'ID' : 'IDs'} on <TourLink id={pending.from_tour_id} name={pending.from_tour_name} />{' '}
        {ids.length === 1 ? 'moves' : 'move'} there:{' '}
        {ids.map((id, i) => (
          <Fragment key={id}>
            {i > 0 && ', '}
            <Code>{id}</Code>
          </Fragment>
        ))}
        . Until then its bookings keep landing on that tour; those already there follow only when the vendor next
        sends them.
      </div>
      {perOption && (
        <div>
          Tours is “one tour per option”: a split-off moves one tour key, so it does not happen (and stays pending)
          until Tours is one tour for the product again.
        </div>
      )}
      <div>{joining ? 'Saving this join cancels the split-off.' : 'Joining a product again cancels it.'}</div>
    </Alert>
  );
}

/** How many refused targets the tenant-defaults alert looks up and names. */
export const TARGETS_NAMED = 10;

/** A tenant-defaults save refused `join_target_in_use` (one error per target, `product_id` its pk):
 *  each target by name with the products that join it (its row's `joined_by`, read by pk; the
 *  server's own detail until then), and what to do first. */
export function TargetsInUseAlert({ tenantId, errors }: { tenantId: number; errors: readonly SettingError[] }) {
  const shown = errors.slice(0, TARGETS_NAMED);
  const targets = useQueries({
    queries: shown.map((e) => ({
      queryKey: catalogueKeys.product(tenantId, e.product_id ?? 0),
      queryFn: ({ signal }: { signal: AbortSignal }) => catalogueApi.getProduct(tenantId, e.product_id!, signal),
      enabled: e.product_id != null,
      retry: false,
    })),
  });
  if (!errors.length) return null;
  return (
    <Alert severity="error" sx={{ mb: 2 }} aria-label="Products others join">
      <AlertTitle>Nothing was saved: products others join would lose their tour</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {shown.map((e, i) => {
          const target = targets[i]?.data;
          const joiners = target?.joined_by ?? [];
          return (
            <li key={`${e.product_id}-${i}`}>
              {target && joiners.length
                ? `${joinerLabel(target.vendor_id, target)}: ${joinersPhrase(joiners)} ` +
                  `${joiners.length === 1 ? 'joins' : 'join'} its tour.`
                : e.detail}
            </li>
          );
        })}
      </Box>
      {errors.length > shown.length && `…and ${errors.length - shown.length} more. `}
      Clear their joins first: this default would take away the one tour their bookings land on.
    </Alert>
  );
}
